import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { cores } from '../../theme';

import {
    Typography, Container, Box, CircularProgress, Alert, Paper, Button, List, ListItem, ListItemText, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField, IconButton, Avatar, Fade,
    ListItemAvatar, Chip, DialogContentText, Tooltip, ListItemSecondaryAction, useTheme
} from '@mui/material';
import {
    ArrowBack,
    PersonAdd as PersonAddIcon,
    School as SchoolIcon,
    Person as PersonIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Delete as DeleteIcon,
    Add as AddIcon,
    Close as CloseIcon,
    Warning as WarningIcon,
    Groups as GroupsIcon,
    Search as SearchIcon
} from '@mui/icons-material';

const GrupoDetalhePagina = () => {
    const { id: grupoId } = useParams();
    const navegar = useNavigate();
    const theme = useTheme();

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');

    const [modalAberto, setModalAberto] = useState(false);
    const [tipoModal, setTipoModal] = useState('');
    const [opcoesBusca, setOpcoesBusca] = useState([]);
    const [selecionado, setSelecionado] = useState(null);
    const [confirmarExclusao, setConfirmarExclusao] = useState({
        aberto: false,
        id: null,
        nome: '',
        tipo: ''
    });

    const buscarDadosDoGrupo = useCallback(async () => {
        try {
            setErro('');
            const resposta = await api.get(`/api/grupos/${grupoId}/detalhes`);
            setDados(resposta.data);
        } catch (err) {
            setErro('Falha ao carregar os detalhes do grupo.');
        } finally {
            if (carregando) setCarregando(false);
        }
    }, [grupoId, carregando]);

    useEffect(() => {
        buscarDadosDoGrupo();
    }, [buscarDadosDoGrupo]);

    const abrirModal = async (cargo) => {
        setSelecionado(null);
        setOpcoesBusca([]);
        setTipoModal(cargo);
        try {
            const turmaId = dados.grupo.turma_id;
            const resposta = await api.get(`/api/turmas/${turmaId}/membros-disponiveis-para-grupo?cargo=${cargo}`);
            setOpcoesBusca(resposta.data);
            setModalAberto(true);
        } catch (err) {
            setErro("Erro ao buscar membros disponíveis.");
        }
    };

    const lidarComAdicionar = async () => {
        if (!selecionado) return;

        const tipo = tipoModal.toLowerCase();
        const url = `/api/grupos/${grupoId}/adicionar-${tipo}`;
        const payload = tipo === 'aluno' ? { aluno_id: selecionado.id } : { professor_id: selecionado.id };

        try {
            await api.post(url, payload);
            setModalAberto(false);
            buscarDadosDoGrupo();
        } catch (err) {
            setErro(`Erro ao adicionar ${tipo}.`);
        }
    };

    const abrirConfirmacaoExclusao = (id, nome, tipo) => {
        setConfirmarExclusao({
            aberto: true,
            id,
            nome,
            tipo
        });
    };

    const fecharConfirmacaoExclusao = () => {
        setConfirmarExclusao({
            aberto: false,
            id: null,
            nome: '',
            tipo: ''
        });
    };

    const lidarComRemoverAluno = async (alunoId) => {
        try {
            await api.delete(`/api/grupos/${grupoId}/alunos/${alunoId}`);
            fecharConfirmacaoExclusao();
            buscarDadosDoGrupo();
        } catch (err) {
            setErro("Erro ao remover aluno.");
            fecharConfirmacaoExclusao();
        }
    };

    const lidarComRemoverProfessor = async (professorId) => {
        try {
            await api.delete(`/api/grupos/${grupoId}/professores/${professorId}`);
            fecharConfirmacaoExclusao();
            buscarDadosDoGrupo();
        } catch (err) {
            setErro("Erro ao remover professor.");
            fecharConfirmacaoExclusao();
        }
    };

    const confirmarRemocao = () => {
        if (confirmarExclusao.tipo === 'aluno') {
            lidarComRemoverAluno(confirmarExclusao.id);
        } else {
            lidarComRemoverProfessor(confirmarExclusao.id);
        }
    };

    if (carregando) {
        return (
            <Container maxWidth="lg" sx={{ py: 5, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: cores.primaria.principal }} />
            </Container>
        );
    }

    if (!dados) {
        return (
            <Container maxWidth="lg" sx={{ py: 4 }}>
                <Alert
                    severity="error"
                    sx={{
                        borderRadius: 2,
                        backgroundColor: cores.erro.claro,
                        borderColor: cores.erro.medio,
                        color: cores.erro.escuro,
                        '& .MuiAlert-icon': {
                            alignItems: 'center'
                        }
                    }}
                >
                    Grupo não encontrado.
                </Alert>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4 } }}>
            <Fade in={true} timeout={500}>
                <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3, boxShadow: cores.sombras.medio }}>
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: 4,
                        p: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        background: cores.gradientes.primario,
                        color: 'white'
                    }}>
                        <IconButton
                            onClick={() => navegar(`/turmas/${dados.grupo.turma_id}`)}
                            sx={{
                                mr: 2,
                                color: 'white',
                                bgcolor: 'rgba(255,255,255,0.2)',
                                '&:hover': {
                                    bgcolor: 'rgba(255,255,255,0.3)',
                                }
                            }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="h4"
                                component="h1"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                                    lineHeight: 1.2
                                }}
                            >
                                Gerenciamento do Grupo
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    opacity: 0.9,
                                    fontSize: { xs: '0.9rem', sm: '1rem' }
                                }}
                            >
                                {dados.grupo.nome}
                            </Typography>
                        </Box>
                    </Box>

                    <Fade in={true} timeout={600}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 2, sm: 3 },
                                mb: 3,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                border: `1px solid ${cores.cinza.claro}`
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Avatar
                                    sx={{
                                        mr: 2,
                                        bgcolor: 'rgba(30, 58, 138, 0.1)',
                                        color: cores.primaria.principal
                                    }}
                                >
                                    <GroupsIcon />
                                </Avatar>
                                <Box>
                                    <Typography variant="overline" sx={{ color: cores.cinza.medio, fontWeight: 600 }}>
                                        INFORMAÇÕES DO GRUPO
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: cores.cinza.escuro }}>
                                        Turma: <span style={{ fontWeight: 600 }}>{dados.grupo.turma_nome}</span>
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Fade>

                    {erro && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                borderRadius: 2,
                                backgroundColor: cores.erro.claro,
                                borderColor: cores.erro.medio,
                                color: cores.erro.escuro,
                                '& .MuiAlert-icon': {
                                    alignItems: 'center'
                                }
                            }}
                        >
                            {erro}
                        </Alert>
                    )}

                    <Fade in={true} timeout={700}>
                        <Paper
                            sx={{
                                mt: 4,
                                p: 0,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: cores.sombras.suave,
                                border: `1px solid ${cores.cinza.claro}`
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: `1px solid ${cores.cinza.claro}`
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SupervisorAccountIcon sx={{ color: cores.primaria.principal, mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        Professores (Tutores)
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => abrirModal('PROFESSOR')}
                                    startIcon={<AddIcon />}
                                    sx={{
                                        bgcolor: cores.primaria.principal,
                                        '&:hover': {
                                            bgcolor: cores.primaria.escuro,
                                        },
                                        borderRadius: 2,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    Adicionar Tutor
                                </Button>
                            </Box>
                            <List sx={{ p: 0 }}>
                                {dados.professores.length === 0 ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ color: cores.cinza.medio, fontStyle: 'italic' }}>
                                            Nenhum professor adicionado a este grupo.
                                        </Typography>
                                    </Box>
                                ) : (
                                    dados.professores.map((prof, i) => (
                                        <React.Fragment key={prof.id}>
                                            <ListItem
                                                sx={{
                                                    px: 3,
                                                    py: 2,
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc'
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: 'rgba(30, 58, 138, 0.1)', color: cores.primaria.principal }}>
                                                        <PersonIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {prof.nome_completo}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Chip
                                                            label="Tutor"
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                mt: 0.5,
                                                                bgcolor: `rgba(${parseInt(cores.primaria.principal.slice(1, 3), 16)}, ${parseInt(cores.primaria.principal.slice(3, 5), 16)}, ${parseInt(cores.primaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                                color: cores.primaria.principal,
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Remover professor">
                                                        <IconButton
                                                            edge="end"
                                                            color="error"
                                                            onClick={() => abrirConfirmacaoExclusao(prof.id, prof.nome_completo, 'professor')}
                                                            sx={{
                                                                color: cores.erro.principal,
                                                                bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.08)`,
                                                                '&:hover': {
                                                                    bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.16)`
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {i < dados.professores.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Fade>

                    <Fade in={true} timeout={800}>
                        <Paper
                            sx={{
                                mt: 4,
                                p: 0,
                                borderRadius: 2,
                                overflow: 'hidden',
                                boxShadow: cores.sombras.suave,
                                border: `1px solid ${cores.cinza.claro}`
                            }}
                        >
                            <Box
                                sx={{
                                    bgcolor: '#f1f5f9',
                                    p: 2,
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: `1px solid ${cores.cinza.claro}`
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <SchoolIcon sx={{ color: cores.primaria.principal, mr: 1 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 600, color: cores.primaria.principal }}>
                                        Alunos do Grupo
                                    </Typography>
                                </Box>
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => abrirModal('ALUNO')}
                                    startIcon={<AddIcon />}
                                    sx={{
                                        bgcolor: cores.primaria.principal,
                                        '&:hover': {
                                            bgcolor: cores.primaria.escuro,
                                        },
                                        borderRadius: 2,
                                        boxShadow: cores.sombras.suave
                                    }}
                                >
                                    Adicionar Aluno
                                </Button>
                            </Box>
                            <List sx={{ p: 0 }}>
                                {dados.alunos.length === 0 ? (
                                    <Box sx={{ p: 3, textAlign: 'center' }}>
                                        <Typography variant="body1" sx={{ color: cores.cinza.medio, fontStyle: 'italic' }}>
                                            Nenhum aluno adicionado a este grupo.
                                        </Typography>
                                    </Box>
                                ) : (
                                    dados.alunos.map((aluno, i) => (
                                        <React.Fragment key={aluno.id}>
                                            <ListItem
                                                sx={{
                                                    px: 3,
                                                    py: 2,
                                                    '&:hover': {
                                                        bgcolor: '#f8fafc'
                                                    }
                                                }}
                                            >
                                                <ListItemAvatar>
                                                    <Avatar sx={{ bgcolor: 'rgba(14, 165, 233, 0.1)', color: cores.secundaria.principal }}>
                                                        <SchoolIcon />
                                                    </Avatar>
                                                </ListItemAvatar>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                                            {aluno.nome_completo}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Chip
                                                            label="Estudante"
                                                            size="small"
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                mt: 0.5,
                                                                bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                                                color: cores.secundaria.principal,
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                    }
                                                />
                                                <ListItemSecondaryAction>
                                                    <Tooltip title="Remover aluno">
                                                        <IconButton
                                                            edge="end"
                                                            color="error"
                                                            onClick={() => abrirConfirmacaoExclusao(aluno.id, aluno.nome_completo, 'aluno')}
                                                            sx={{
                                                                color: cores.erro.principal,
                                                                bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.08)`,
                                                                '&:hover': {
                                                                    bgcolor: `rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.16)`
                                                                }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {i < dados.alunos.length - 1 && <Divider />}
                                        </React.Fragment>
                                    ))
                                )}
                            </List>
                        </Paper>
                    </Fade>
                </Paper>
            </Fade>

            {/* Modal para adicionar aluno/professor */}
            <Dialog
                open={modalAberto}
                onClose={() => setModalAberto(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pb: 1
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                            sx={{
                                bgcolor: 'rgba(30, 58, 138, 0.1)',
                                color: cores.primaria.principal
                            }}
                        >
                            <PersonAddIcon />
                        </Avatar>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Adicionar {tipoModal === 'ALUNO' ? 'Aluno' : 'Professor'} ao Grupo
                        </Typography>
                    </Box>
                    <IconButton
                        edge="end"
                        onClick={() => setModalAberto(false)}
                        size="small"
                        sx={{ color: cores.cinza.medio }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ pt: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            mb: 2,
                            color: cores.cinza.medio
                        }}
                    >
                        Selecione o {tipoModal === 'ALUNO' ? 'aluno' : 'professor'} que deseja adicionar a este grupo.
                    </Typography>

                    <Autocomplete
                        options={opcoesBusca}
                        getOptionLabel={(option) => option.nome_completo || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        onChange={(e, v) => setSelecionado(v)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={`Buscar ${tipoModal.toLowerCase()}`}
                                margin="normal"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <>
                                            <SearchIcon sx={{ color: cores.cinza.medio, mr: 1 }} />
                                            {params.InputProps.startAdornment}
                                        </>
                                    ),
                                    sx: {
                                        borderRadius: 2
                                    }
                                }}
                            />
                        )}
                        noOptionsText={`Nenhum ${tipoModal.toLowerCase()} disponível.`}
                    />

                    {opcoesBusca.length === 0 && (
                        <Paper
                            elevation={0}
                            sx={{
                                mt: 2,
                                p: 2,
                                borderRadius: 2,
                                bgcolor: `rgba(${parseInt(cores.secundaria.principal.slice(1, 3), 16)}, ${parseInt(cores.secundaria.principal.slice(3, 5), 16)}, ${parseInt(cores.secundaria.principal.slice(5, 7), 16)}, 0.1)`,
                                border: `1px solid ${cores.secundaria.claro}`,
                            }}
                        >
                            <Typography variant="body2" sx={{ color: cores.secundaria.principal }}>
                                <strong>Nota:</strong> Somente {tipoModal === 'ALUNO' ? 'alunos' : 'professores'} que já foram adicionados à turma e ainda não estão em nenhum grupo aparecerão na lista.
                            </Typography>
                        </Paper>
                    )}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button
                        onClick={() => setModalAberto(false)}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            borderColor: cores.cinza.claro,
                            color: cores.cinza.escuro,
                            '&:hover': {
                                borderColor: cores.cinza.medio,
                                bgcolor: 'rgba(203, 213, 225, 0.1)'
                            }
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={lidarComAdicionar}
                        variant="contained"
                        disabled={!selecionado}
                        startIcon={<AddIcon />}
                        sx={{
                            borderRadius: 2,
                            bgcolor: cores.primaria.principal,
                            fontWeight: 600,
                            '&:hover': {
                                bgcolor: cores.primaria.escuro,
                            },
                            '&.Mui-disabled': {
                                bgcolor: cores.cinza.medio,
                                color: 'white'
                            }
                        }}
                    >
                        Adicionar ao Grupo
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Diálogo de confirmação de exclusão */}
            <Dialog
                open={confirmarExclusao.aberto}
                onClose={fecharConfirmacaoExclusao}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: cores.sombras.forte,
                        maxWidth: 450
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: cores.erro.claro,
                    color: cores.erro.escuro,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 700,
                    py: 2
                }}>
                    <WarningIcon color="error" fontSize="small" />
                    Confirmar Remoção
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 1 }}>
                    <DialogContentText sx={{ color: cores.cinza.escuro, mb: 2 }}>
                        Você está prestes a remover {confirmarExclusao.tipo === 'aluno' ? 'o aluno' : 'o professor'}:
                    </DialogContentText>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            bgcolor: '#f8fafc',
                            borderRadius: 2,
                            border: `1px solid ${cores.cinza.claro}`,
                            mb: 2
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 600,
                                color: cores.cinza.escuro,
                                fontStyle: 'italic'
                            }}
                        >
                            "{confirmarExclusao.nome}"
                        </Typography>
                    </Paper>
                    <DialogContentText sx={{
                        color: cores.erro.escuro,
                        bgcolor: cores.erro.claro,
                        p: 2,
                        borderRadius: 2,
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <WarningIcon fontSize="small" sx={{ color: cores.erro.medio }} />
                        {confirmarExclusao.tipo === 'aluno'
                            ? "Esta ação removerá o aluno deste grupo, mas ele permanecerá na turma."
                            : "Esta ação removerá o professor deste grupo, mas ele permanecerá como tutor da turma."
                        }
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                    <Button
                        onClick={fecharConfirmacaoExclusao}
                        variant="outlined"
                        sx={{
                            borderRadius: 2,
                            color: cores.cinza.escuro,
                            borderColor: cores.cinza.claro,
                            '&:hover': {
                                backgroundColor: '#f1f5f9',
                                borderColor: cores.cinza.medio
                            },
                            px: 2
                        }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={confirmarRemocao}
                        variant="contained"
                        color="error"
                        sx={{
                            borderRadius: 2,
                            fontWeight: 600,
                            boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.2)`,
                            px: 3,
                            '&:hover': {
                                backgroundColor: cores.erro.escuro,
                                boxShadow: `0 4px 12px rgba(${parseInt(cores.erro.principal.slice(1, 3), 16)}, ${parseInt(cores.erro.principal.slice(3, 5), 16)}, ${parseInt(cores.erro.principal.slice(5, 7), 16)}, 0.3)`
                            }
                        }}
                    >
                        Remover {confirmarExclusao.tipo === 'aluno' ? 'Aluno' : 'Professor'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default GrupoDetalhePagina;